// MiniBreakEvenPlane.js
import React, { useMemo } from "react";
import CoordinatePlane from "./CoordinatePlane";

/** 
 * Approximate y=0.1x^2 +10x +200 in small segments,
 * plus the line y=25x, from x=0..220.
 * We'll also compute their intersection points
 * and show them as red dots (extraPoints).
 */
export default function MiniBreakEvenPlane() {
  // 1) Build the cost function as line segments
  const costSegments = useMemo(() => {
    const segs = [];
    const step = 2;
    for (let x = 0; x < 220; x += step) {
      const nx = x + step;
      const yA = 0.1 * x * x + 10*x + 200;
      const yB = 0.1 * nx * nx + 10*nx + 200;
      segs.push({
        start: [x, yA],
        end:   [nx, yB],
        color: "blue"
      });
    }
    return segs;
  }, []);

  // Revenue line from x=0..220
  const revenueLine = useMemo(() => {
    return [{
      start: [0, 0],
      end:   [220, 25*220], // 25*220=5500
      color: "green",
      label: "R(x)=25x"
    }];
  }, []);

  // Solve  25x = 0.1x^2 +10x+200  => 0.1x^2-15x+200=0
  // That yields ~ x=10.7 or x=186.6
  const solutions = useMemo(() => {
    const A=0.1, B=-15, C=200;
    const disc = B*B - 4*A*C; //225 -80=145
    if (disc < 0) return [];
    const sqrtD = Math.sqrt(disc);
    const x1 = (-B - sqrtD)/(2*A);
    const x2 = (-B + sqrtD)/(2*A);
    const pts = [];
    [x1,x2].forEach(x => {
      if (x>=0 && x<=220) {
        pts.push({ x, y: 25*x });
      }
    });
    return pts;
  }, []);

  // 4) Combine costSegments + revenueLine into one array
  const allLines = [...costSegments, ...revenueLine];

  return (
    <CoordinatePlane
      width={400}
      height={300}
      /* Key difference: show the entire 0..220 x range, up to ~8000 in y */
      xDomain={[0, 220]}
      yDomain={[0, 8000]}
      axisMode="border"
      showAxes={true}
      disablePlot={true}
      lines={allLines}
      extraPoints={solutions}  // red points at intersections
    />
  );
}
